import React, { useRef, useEffect } from 'react';
import { CanvasComment } from '../types';

interface CommentPopoverProps {
  comment: CanvasComment;
  position: { x: number; y: number };
  currentUser: string;
  onResolve: () => void;
  onUnresolve: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * Popover showing comment details with actions.
 * Positioned near the comment bubble.
 */
const CommentPopover: React.FC<CommentPopoverProps> = ({
  comment,
  position,
  currentUser,
  onResolve,
  onUnresolve,
  onDelete,
  onClose,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    // Add listener with slight delay to avoid immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  // Format timestamp
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  const isAuthor = comment.author === currentUser;
  
  // Calculate position to ensure dialog stays within viewport
  const dialogWidth = 300;
  const padding = 20;
  
  let left = position.x + 20;
  let top = position.y - 10;
  
  // Adjust if going off right edge
  if (left + dialogWidth > window.innerWidth - padding) {
    left = position.x - dialogWidth - 20;
  }
  // Adjust if going off top
  if (top < padding) {
    top = padding;
  }
  
  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 1000,
        minWidth: 280,
        maxWidth: 360,
        background: 'white',
        borderRadius: 10,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #e5e7eb',
          background: comment.resolved ? '#f3f4f6' : '#fffbeb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Author avatar */}
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {comment.author.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
              {comment.author}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {formatDate(comment.created_at)}
            </div>
          </div>
        </div>
        
        {/* Status badge */}
        {comment.resolved && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: 4,
              background: '#d1fae5',
              color: '#047857',
            }}
          >
            Resolved
          </span>
        )}
      </div>
      
      {/* Content */}
      <div style={{ padding: '12px', fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
        {comment.text}
      </div>
      
      {/* Resolved info */}
      {comment.resolved && comment.resolved_by && (
        <div
          style={{
            padding: '8px 12px',
            fontSize: 11,
            color: '#6b7280',
            background: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          Resolved by <strong>{comment.resolved_by}</strong>
          {comment.resolved_at && ` on ${formatDate(comment.resolved_at)}`}
        </div>
      )}
      
      {/* Actions */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        {comment.resolved ? (
          <button
            onClick={onUnresolve}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 6,
              border: '1px solid #d1d5db',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            Re-open
          </button>
        ) : (
          <button
            onClick={onResolve}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 6,
              border: 'none',
              background: '#10b981',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Resolve
          </button>
        )}
        
        {isAuthor && (
          <button
            onClick={onDelete}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 6,
              border: '1px solid #fecaca',
              background: '#fef2f2',
              color: '#dc2626',
              cursor: 'pointer',
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default CommentPopover;
